import type { LucideIcon } from "lucide-react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface KpiCardProps {
  title: string;
  value: string;
  icon: LucideIcon;
  iconClassName?: string;
  /** Percent change vs the previous period. null means "no prior data". */
  changePct?: number | null;
  changeLabel?: string;
  /** Optional secondary line under the value (e.g. "12 in last 30 days"). */
  subValue?: string;
}

export function KpiCard({
  title,
  value,
  icon: Icon,
  iconClassName = "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  changePct,
  changeLabel = "vs prev. 30 days",
  subValue,
}: KpiCardProps) {
  const hasChange = changePct !== undefined && changePct !== null;
  const isUp = hasChange && (changePct as number) >= 0;
  const TrendIcon = !hasChange ? Minus : isUp ? ArrowUpRight : ArrowDownRight;
  const trendColor = !hasChange
    ? "text-slate-400"
    : isUp
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-red-600 dark:text-red-400";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">
          {title}
        </CardTitle>
        <div className={`p-2 rounded-lg ${iconClassName}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold tracking-tight">{value}</div>
        {subValue && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {subValue}
          </p>
        )}
        <div className={`flex items-center gap-1 mt-2 text-xs ${trendColor}`}>
          <TrendIcon className="h-3.5 w-3.5" />
          <span className="font-medium">
            {hasChange ? `${isUp ? "+" : ""}${changePct}%` : "—"}
          </span>
          <span className="text-slate-400 dark:text-slate-500">
            {changeLabel}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
